import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import {
  useKeepAlertSession,
  useReleaseAlertSession,
} from '@/hooks/api/useAgentSessionTimeline';
import {
  KEEP_SESSION_TOOLTIP,
  KEEP_UNAVAILABLE_HINT,
  KEPT_SESSION_TOOLTIP,
  RELEASE_SESSION_CONFIRM_DESCRIPTION,
  RELEASE_SESSION_CONFIRM_TITLE,
} from './session-timeline-display';

export interface KeepSessionButtonProps {
  organizationId: string | null;
  occurrenceId: string;
  /** Whether the session row exists. Keep never creates it. */
  recorded: boolean;
  retained: boolean;
}

/**
 * "Keep session events" (KIR-4445): exempts the session from retention, and a
 * second press lifts the hold after a confirmation. Disabled only when the
 * session row is absent — not when the alert's own event is pending or
 * missing, since other events may already have created it.
 */
export function KeepSessionButton({
  organizationId,
  occurrenceId,
  recorded,
  retained,
}: KeepSessionButtonProps) {
  // The backend allows Keep and release to managers and owners only in an
  // organization; a personal session belongs to the viewer.
  const { hasAccess } = useRoleCheck(
    organizationId
      ? { param: 'organizationId', anyOf: ['owner', 'manager'] }
      : undefined,
    organizationId ?? undefined
  );
  const keep = useKeepAlertSession(organizationId, occurrenceId);
  const release = useReleaseAlertSession(organizationId, occurrenceId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const busy = keep.isPending || release.isPending;

  const onKeep = () => {
    release.reset();
    keep.mutate();
  };
  const onRelease = () => {
    keep.reset();
    release.mutate(undefined, { onSettled: () => setConfirmOpen(false) });
  };

  if (!hasAccess) return null;

  return (
    <div className="flex shrink-0 flex-col items-start gap-1">
      <Tooltip>
        {/* A disabled button fires no pointer events; the wrapper does. */}
        <TooltipTrigger asChild>
          <span tabIndex={recorded ? undefined : 0}>
            {retained ? (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => setConfirmOpen(true)}
                data-testid="session-kept"
              >
                <Check className="size-4" aria-hidden />
                Session events kept
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={!recorded || busy}
                onClick={onKeep}
              >
                Keep session events
              </Button>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {retained ? KEPT_SESSION_TOOLTIP : KEEP_SESSION_TOOLTIP}
        </TooltipContent>
      </Tooltip>
      {!recorded && !retained && (
        <span className="text-xs text-muted-foreground">
          {KEEP_UNAVAILABLE_HINT}
        </span>
      )}
      {keep.isError && (
        <span className="text-xs text-destructive">
          Could not keep this session.
        </span>
      )}
      {release.isError && (
        <span className="text-xs text-destructive">
          Could not stop keeping this session.
        </span>
      )}
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onRelease}
        title={RELEASE_SESSION_CONFIRM_TITLE}
        description={RELEASE_SESSION_CONFIRM_DESCRIPTION}
        confirmLabel="Stop keeping"
        confirmingLabel="Stopping…"
        isLoading={release.isPending}
      />
    </div>
  );
}
